import * as React from "react";
import { midpointApiService, Group } from "../services/midpointApi";
import { MidpointAuthenticationError } from "../services/midpointAuth";
import Loader from "../components/Loader";
import { Error as ErrorDisplay } from "../components/Error";
import NoGroupsAssigned from "../components/NoGroupsAssigned";
import NoSearchResult from "../components/NoSearchResult";
import Card from "../components/Card";
import Pagination from "../components/Pagination";
import { AdContext } from "../context/AdContext";
import "../styles/dashboard.css";

const PAGE_SIZE = 10;
const api_url_endpoint = process.env.REACT_APP_API_URL;
const content_type = process.env.REACT_APP_CONTENT_TYPE;
const useMidpoint = process.env.REACT_APP_USE_MIDPOINT === "true";

async function fetchGroupsPage(
  email: string,
  page: number,
  search: string
): Promise<{ groups: Group[]; total: number }> {
  if (useMidpoint) {
    try {
      const assignedRoles = await midpointApiService.getUserAssignedRoles(email);
      const filtered = search
        ? assignedRoles.filter((g) =>
            g.displayName.toLowerCase().includes(search.toLowerCase())
          )
        : assignedRoles;
      const start = (page - 1) * PAGE_SIZE;
      return {
        groups: filtered.slice(start, start + PAGE_SIZE),
        total: filtered.length,
      };
    } catch (err) {
      if (err instanceof MidpointAuthenticationError) {
        throw new Error(`Authentication Error: ${err.message}`);
      }
      throw err;
    }
  } else {
    const response = await fetch(
      `${api_url_endpoint}/ad-management/ad-handler/get-owner-groups`,
      {
        method: "POST",
        headers: { "Content-Type": content_type || "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    const json = await response.json();
    const all: Group[] = json?.data?.groupDetails ?? [];
    const filtered = search
      ? all.filter((g) => g.displayName.toLowerCase().includes(search.toLowerCase()))
      : all;
    const start = (page - 1) * PAGE_SIZE;
    return { groups: filtered.slice(start, start + PAGE_SIZE), total: filtered.length };
  }
}

const CardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <rect x="0.5" y="0.5" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="8.5" y="0.5" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="0.5" y="8.5" width="6" height="6" rx="1" fill="currentColor" />
    <rect x="8.5" y="8.5" width="6" height="6" rx="1" fill="currentColor" />
  </svg>
);

const TableIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
    <rect x="0.5" y="0.5" width="14" height="3" rx="1" fill="currentColor" />
    <rect x="0.5" y="5.5" width="14" height="3" rx="1" fill="currentColor" />
    <rect x="0.5" y="10.5" width="14" height="3" rx="1" fill="currentColor" />
  </svg>
);

const Dashboard = ({ email }: { email: string }) => {
  const { navigateToGroup } = React.useContext(AdContext);
  const [view, setView] = React.useState<"card" | "table">("card");
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [groups, setGroups] = React.useState<Group[]>([]);
  const [total, setTotal] = React.useState(0);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [pageLoading, setPageLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Debounce search input
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPageLoading(true);
      setError(null);
      try {
        const result = await fetchGroupsPage(email, page, debouncedSearch);
        if (!cancelled) {
          setGroups(result.groups);
          setTotal(result.total);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load groups");
      } finally {
        if (!cancelled) {
          setPageLoading(false);
          setInitialLoad(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [email, page, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [pagination, setPagination] = React.useState<Record<string, any>>({
    totalResults: 0, totalPages: 1, page: 1, limit: PAGE_SIZE,
  });

  React.useEffect(() => {
    setPagination({ totalResults: total, totalPages, page, limit: PAGE_SIZE });
  }, [total, totalPages, page]);

  if (initialLoad && pageLoading) return <Loader />;
  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, padding: 40 }}>
      <div style={{ color: "var(--red)", fontSize: 14 }}>{error}</div>
      <button
        onClick={() => { setError(null); setPage(1); }}
        style={{ background: "var(--accent-dim)", color: "var(--accent)", border: "1px solid rgba(59,130,246,0.25)", borderRadius: 6, padding: "7px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
      >
        Retry
      </button>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, width: "100%" }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 19, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.3px" }}>
            AD Group Management
          </div>
          {pageLoading && !initialLoad && (
            <div style={{
              width: 13, height: 13,
              border: "2px solid var(--border)",
              borderTop: "2px solid var(--accent)",
              borderRadius: "50%",
              animation: "spin 0.7s linear infinite",
            }} />
          )}
        </div>
        <div className="adgc-search">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 16 16" fill="none">
            <path d="M15.5 15.5L12.5 12.5" stroke="#4A5B70" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6.5 12.5C9.81371 12.5 12.5 9.81371 12.5 6.5C12.5 3.18629 9.81371 0.5 6.5 0.5C3.18629 0.5 0.5 3.18629 0.5 6.5C0.5 9.81371 3.18629 12.5 6.5 12.5Z" stroke="#4A5B70" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <input
            placeholder="Search groups..."
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Sub-header: count + view toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Groups</div>
          <span style={{
            background: "var(--accent-dim)", color: "var(--accent)",
            fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
          }}>
            {total}
          </span>
        </div>
        <div style={{
          display: "flex", padding: "3px", gap: "2px",
          background: "var(--bg-elevated)", borderRadius: "6px",
          border: "1px solid var(--border)",
        }}>
          <button
            onClick={() => setView("card")}
            title="Card view"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 26, border: "none", borderRadius: "4px", cursor: "pointer",
              background: view === "card" ? "var(--accent-dim)" : "transparent",
              color: view === "card" ? "var(--accent)" : "var(--text-muted)",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            <CardIcon />
          </button>
          <button
            onClick={() => setView("table")}
            title="Table view"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 28, height: 26, border: "none", borderRadius: "4px", cursor: "pointer",
              background: view === "table" ? "var(--accent-dim)" : "transparent",
              color: view === "table" ? "var(--accent)" : "var(--text-muted)",
              transition: "background 0.15s, color 0.15s",
            }}
          >
            <TableIcon />
          </button>
        </div>
      </div>

      {/* Content */}
      {pageLoading && !initialLoad ? (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          minHeight: 200, gap: 12, color: "var(--text-muted)", fontSize: 13,
        }}>
          <div style={{
            width: 16, height: 16,
            border: "2px solid var(--border)",
            borderTop: "2px solid var(--accent)",
            borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }} />
          Loading groups...
        </div>
      ) : groups.length === 0 && total === 0 && !debouncedSearch ? (
        <NoGroupsAssigned a={"No assigned groups"} />
      ) : groups.length === 0 ? (
        <NoSearchResult />
      ) : view === "card" ? (
        <div className="adgc-wrapper">
          {groups.map((group, i) => <Card key={group.id || i} group={group} />)}
        </div>
      ) : (
        <div className="groups-table-container">
          <table className="groups-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Description</th>
                <th>Created</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group, i) => (
                <tr
                  key={group.id || i}
                  className="groups-table-row"
                  onClick={() => navigateToGroup(group)}
                >
                  <td className="groups-table-index">{(page - 1) * PAGE_SIZE + i + 1}</td>
                  <td className="groups-table-name">{group.displayName}</td>
                  <td className="groups-table-desc">{group.description || "—"}</td>
                  <td className="groups-table-date">
                    {group.createdDateTime
                      ? new Date(group.createdDateTime).toLocaleDateString("en-US", {
                          year: "numeric", month: "short", day: "numeric",
                        })
                      : "—"}
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="groups-table-action-btn"
                      onClick={(e) => { e.stopPropagation(); navigateToGroup(group); }}
                    >
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {(totalPages > 1 || page > 1) && (
        <div style={{ marginTop: 8 }}>
          <Pagination
            setcurrentPage={setPage}
            pagination={pagination}
            setPagination={setPagination}
            limitNum={PAGE_SIZE}
            setLimitNum={() => {}}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
